import { useState, useRef, useEffect } from "react";
import { Box, Stack, Popper } from "@mui/material";
import { CustomButton, CustomTypography } from "src/components/inputs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { GridActionsCellItem } from "@mui/x-data-grid";
import moment from "moment";
import EmailIcon from "@mui/icons-material/Email";
import parse from "html-react-parser";

export const InboundRequestViewEmailHistoryModal = (props) => {
  const modalRef = useRef(null);
  const [emailBodyAnchorEl, setEmailBodyAnchorEl] = useState(null);
  const [currentEmailBody, setCurrentEmailBody] = useState("");
  const open = Boolean(emailBodyAnchorEl);
  const inboundRequestEmails = props.histories?.inboundRequestEmails || [];
  const isOpen = props?.isOpen;

  const handleViewEmailBodyClick = (event, emailBody) => {
    setEmailBodyAnchorEl(emailBodyAnchorEl ? null : event.currentTarget);
    setCurrentEmailBody(parse(emailBody));
  };

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isOpen]);

  const columns = [
    {
      field: "createdAt",
      headerName: "Date & Time",
      headerClassName: "first-header-style header-style",
      valueFormatter: (params) => {
        if (params?.value) {
          return moment(params?.value).format("DD.MM.YYYY, HH:mm:ss");
        }
      },
      cellClassName: "centered-text",
      width: 175,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "type",
      headerName: "Type",
      headerClassName: "header-style",
      cellClassName: "centered-text",
      width: 225,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "to",
      headerName: "To",
      headerClassName: "header-style",
      cellClassName: "centered-text",
      width: 200,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "cc",
      headerName: "CC",
      headerClassName: "header-style",
      cellClassName: "centered-text",
      width: 175,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "bcc",
      headerName: "BCC",
      headerClassName: "header-style",
      cellClassName: "centered-text",
      width: 175,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "subject",
      headerName: "Subject",
      headerClassName: "header-style",
      cellClassName: "centered-text",
      flex: 2,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "emailBody",
      headerName: "Email Body",
      headerClassName: "last-header-style header-style",
      cellClassName: "centered-text",
      flex: 1,
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <span style={{ textAlign: "center", width: "100%" }}>
            <GridActionsCellItem
              style={{ color: "black" }}
              icon={<EmailIcon />}
              key={"viewPOEmailBody-" + params.row.id}
              onClick={(e) => handleViewEmailBodyClick(e, params.row.body)}
            />
            <Popper
              style={{ zIndex: 1200 }}
              id={params.id}
              open={open}
              anchorEl={emailBodyAnchorEl}
            >
              <Box
                sx={{
                  background: "white",
                  border: "1px solid #E6E6E6",
                  padding: "10px 20px",
                  width: "500px",
                  fontFamily: "tmwCustomFont",
                  fontSize: 14,
                }}
              >
                {currentEmailBody}
              </Box>
            </Popper>
          </span>
        );
      },
    },
  ];

  const dataGridStyle = {
    fontFamily: "tmwCustomFont",
    "& .MuiDataGrid-columnHeaders": {
      borderRadius: 0,
      borderBottom: "1px solid #E6E6E6",
    },
    "& .MuiInputBase-input": {
      fontSize: 12,
      fontWeight: 500,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
    },
    "& .MuiDataGrid-columnHeader--emptyGroup": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:not(:first-of-type)": {
      borderLeft: "1px solid #E6E6E6",
      outline: "none",
    },
    "& .MuiDataGrid-cell": {
      borderLeft: "1px solid #E6E6E6",
      letterSpacing: 1,
      fontSize: 12,
      fontWeight: 400,
      outline: "none !important",
    },
    "& .MuiDataGrid-cell:last-child": {
      borderRight: "1px solid #E6E6E6",
    },
    "& .MuiDataGrid-row:not(:last-child)": {
      paddingTop: "1px",
      borderBottom: "1px solid #E6E6E6",
    },
    "& .MuiDataGrid-columnSeparator": {
      opacity: "0 !important",
    },
    "& .first-header-style": {
      borderTop: "1px solid #E6E6E6",
      borderLeft: "1px solid #E6E6E6 !important",
      outline: "none !important",
    },
    "& .last-header-style": {
      borderTop: "1px solid #E6E6E6",
      borderRight: "1px solid #E6E6E6 !important",
    },
    "& .header-style": {
      borderTop: "1px solid #E6E6E6",
      letterSpacing: 2,
      fontSize: 10,
      fontWeight: 600,
      textTransform: "uppercase",
    },
    "& .centered-text": {
      display: "flex",
      justifyContent: "center",
      fontSize: 11,
    },
    "& .actions": {
      borderLeft: "1px solid #E6E6E6",
      display: "flex",
      justifyContent: "space-around",
    },
    "& .header-actions": {
      fontSize: 7,
    },
    "& .MuiDataGrid-footerContainer": {
      letterSpacing: 2,
      textTransform: "uppercase",
    },
    "& .MuiTablePagination-selectLabel": {
      fontSize: 11,
      letterSpacing: 1,
    },
    "& .MuiTablePagination-displayedRows": {
      fontSize: 11,
      letterSpacing: 1,
    },
    "& .MuiTablePagination-select": {
      fontSize: 11,
      letterSpacing: 1,
    },
    "& .MuiDataGrid-virtualScroller": {
      borderBottom: "1px solid #E6E6E6",
      letterSpacing: 2,
      fontSize: 10,
      fontWeight: 600,
      minHeight: "50px",
    },
    "& .invalid": {
      border: "1px solid red",
    },
    flex: 1,
    "& .light-grey": {
      backgroundColor: "rgba(211, 211, 211)",
    },
    "& .colour-variant": {
      backgroundColor: "rgba(255, 255, 237)",
      "&:hover": {
        backgroundColor: "rgba(248, 248, 235)",
      },
      "&.Mui-selected": {
        backgroundColor: "rgba(248, 248, 235)",
        "&:hover": {
          backgroundColor: "rgba(248, 248, 235)",
        },
      },
    },
    "& .carryover": {
      backgroundColor: "rgba(237, 255, 240)",
      "&:hover": {
        backgroundColor: "rgba(235, 248, 238)",
      },
      "&.Mui-selected": {
        backgroundColor: "rgba(235, 248, 238)",
        "&:hover": {
          backgroundColor: "rgba(235, 248, 238)",
        },
      },
    },
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backdropFilter: "blur(5px)",
        zIndex: 1100,
      }}
    >
      <Box
        ref={modalRef}
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "relative",
          top: "45%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1100,
        }}
      >
        <Stack style={{ width: "90%" }}>
          <Stack
            elevation={12}
            style={{
              background: "white",
              borderRadius: "25px",
              border: "1px solid #E6E6E6",
            }}
          >
            <Stack
              style={{
                height: "40px",
                background: "green",
                borderRadius: "25px 25px 0 0",
                width: "100%",
              }}
            />
            <Stack
              direction="row"
              spacing={2}
              sx={{ display: "flex", p: 3, width: "100%" }}
            >
              <Stack
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div style={{ width: "100%" }}>
                  <CustomTypography
                    variant="h5"
                    message={props?.title || "Inbound Request Email History"}
                    title
                    style={{ marginLeft: "20px" }}
                  />
                  <div style={{ marginTop: "25px" }}>
                    {props.histories && (
                      <DataGridPro
                        disableColumnFilter
                        disableDensitySelector
                        sx={dataGridStyle}
                        rows={inboundRequestEmails}
                        columns={columns}
                        pagination
                        disableColumnSelector
                        rowSelection={false}
                        slotProps={{
                          toolbar: {
                            style: {
                              marginTop: "-75px",
                              marginBottom: "25px",
                              marginLeft: 0,
                              display: "flex",
                              flexDirection: "row-reverse",
                              width: "25%",
                            },
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                          },
                          pagination: {
                            SelectProps: {
                              MenuProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    textTransform: "uppercase",
                                    fontSize: 11,
                                    letterSpacing: 1,
                                  },
                                },
                              },
                            },
                          },
                        }}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 10, page: 0 },
                          },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        // getRowHeight={() => 48}
                        // autoHeight
                      />
                    )}
                  </div>
                </div>
              </Stack>
            </Stack>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pb: 3,
                px: 3,
                gap: 2,
              }}
            >
              <CustomButton
                label={props?.cancelButtonLabel || "Close"}
                color="inherit"
                sx={{ mr: 2 }}
                onClick={() => props?.cancelButton()}
              />
            </Box>
            <Popper
              open={open}
              anchorEl={emailBodyAnchorEl}
              placement="bottom-start"
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: 3,
                  maxWidth: 600,
                }}
              >
                <CustomTypography message="Email Body" title />
                <div>{currentEmailBody}</div>
                <CustomButton
                  label="Close"
                  onClick={() => setEmailBodyAnchorEl(null)}
                  style={{ marginTop: "10px", width: "100px" }}
                />
              </Box>
            </Popper>
          </Stack>
        </Stack>
      </Box>
    </div>
  );
};
